import { useState, useEffect } from "react";
import "../../styles/BookingView.css";
import { authStore } from "../../stores/AuthStore";
import { bookingStore } from "../../stores/BookingStore";
import { TCalendarBooking, TLodging } from "../../types/types";
import { differenceInDays, format, set } from "date-fns";
import {
  calculateTotalGuests,
  calculateTotalPrice,
} from "../../utils/functions";
import BookingPricingStructure from "./BookingPricingStructure";
import { BookingPayments } from "./BookingPayments";
import { ConfirmationModal } from "../common/ConfirmationModal";

export const BookingView = (props: {
  booking: TCalendarBooking;
  selectBookingToView: Function;
  clearBookingToView: Function;
  getViewDays: Function;
  selectedDate: Date | null;
}) => {
  const [editedBooking, setEditedBooking] = useState(props.booking);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const totalPrice = editedBooking.pricing_structure_json?.total_price || 0;
  const totalPaid = editedBooking.payments
    ? editedBooking.payments.reduce((sum, payment) => sum + payment.amount * 1, 0)
    : 0;
  const balanceDue = (totalPrice) - totalPaid;

  const [paymentAmount, setPaymentAmount] = useState(
    balanceDue === totalPrice ? totalPrice * 0.5 : balanceDue,
  );
  const [paymentReference, setPaymentReference] = useState(balanceDue === totalPrice ? "Deposit" : "Additional Payment");
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showCaptureDepositConfirm, setShowCaptureDepositConfirm] = useState(false);
  const [showMarkConfirmedConfirm, setShowMarkConfirmedConfirm] = useState(false);

  // Initialize default dates for new bookings
  useEffect(() => {
    // Only set defaults for new bookings (no booking_id)
    if (!props.booking.booking_id) {
      const startDate = format(props.selectedDate ?? new Date(), "yyyy-MM-dd");
      const endDate = format(
        props.selectedDate
          ? new Date(
            new Date(props.selectedDate).getTime() + 24 * 60 * 60 * 1000,
          )
          : new Date(new Date().getTime() + 24 * 60 * 60 * 1000),
        "yyyy-MM-dd",
      );
      setEditedBooking((prevBooking) => ({
        ...prevBooking,
        start_date: startDate,
        end_date: endDate,
        nights: 1,
      }));
    }
  }, [props.booking.booking_id, props.selectedDate]); // Only runs when booking changes or date selection changes

  const handlePricingStructureChange = (
    value: any,
    pricing_structure_id: string = "",
    selectedPricingKey = "",
  ) => {
    const selectedLodging: TLodging | undefined = authStore
      .getLodgings()
      .find((l: TLodging) => l.id === editedBooking.lodging_id);
    const newObject = {
      ...editedBooking.pricing_structure_json,
      [pricing_structure_id]: value,
    };

    const guests = calculateTotalGuests(newObject);
    const nights = differenceInDays(
      new Date(editedBooking.end_date),
      new Date(editedBooking.start_date),
    );
    const totalPrice = calculateTotalPrice(
      newObject,
      nights,
      selectedLodging?.pricing_structure_json,
    );
    // console.log(">>>",editedBooking);
    setEditedBooking({
      ...editedBooking,
      guests,
      nights,
      pricing_structure_json: {
        ...newObject,
        total_price: totalPrice,
        selected_pricing: selectedPricingKey ?? "",
      },
    } as TCalendarBooking);
    setSaveError(null);
  };

  const handleLodgingChange = (lodgingId: number) => {
    const selectedLodging: TLodging | undefined = authStore
      .getLodgings()
      .find((l: TLodging) => l.id === lodgingId);
    if (selectedLodging) {
      const lodgingType = selectedLodging.pricing_structure_json.campsite
        ? "campsite"
        : selectedLodging.pricing_structure_json.unit
          ? "unit"
          : "persons-1";
      setEditedBooking({
        ...editedBooking,
        lodging_id: lodgingId,
        guests: 0, // reset guests to 0 when lodging changes
        pricing_structure_json:
          selectedLodging.pricing_structure_json[lodgingType],
      } as TCalendarBooking);
    }
    setSaveError(null);
  };

  /**
   * Get available lodgings for the selected date range
   * Only returns lodgings that are available for ALL dates in the range
   */
  const getAvailableLodgingsForDateRange = (): number[] => {
    if (!editedBooking.start_date || !editedBooking.end_date) {
      return authStore.getLodgings().map((l: TLodging) => l.id);
    }

    const availability = bookingStore.calendarAvailability || [];

    if (!availability || availability.length === 0) {
      return authStore.getLodgings().map((l: TLodging) => l.id);
    }

    // Get all unique dates in the range from availability data
    const datesInRange = new Set<string>();
    const lodgingAvailability = new Map<number, Set<string>>();

    availability.forEach((slot: any) => {
      const slotDate = slot.calendar_date;

      // Only consider availability slots within our selected date range
      if (slotDate >= editedBooking.start_date && slotDate < editedBooking.end_date) {
        datesInRange.add(slotDate);

        const lodgingId = slot.lodging_id;
        if (!lodgingAvailability.has(lodgingId)) {
          lodgingAvailability.set(lodgingId, new Set());
        }
        lodgingAvailability.get(lodgingId)!.add(slotDate);
      }
    });

    // Only return lodgings that are available for all dates in the range
    const availableLodgingIds: number[] = [];
    const requiredDates = datesInRange.size;

    lodgingAvailability.forEach((dates, lodgingId) => {
      if (dates.size === requiredDates) {
        availableLodgingIds.push(lodgingId);
      }
    });

    return availableLodgingIds.length > 0
      ? availableLodgingIds
      : authStore.getLodgings().map((l: TLodging) => l.id);
  };

  const handleDateChange = (
    field: "start_date" | "end_date",
    value: string,
  ) => {
    const selectedLodging: TLodging | undefined = authStore
      .getLodgings()
      .find((l: TLodging) => l.id === editedBooking.lodging_id);
    let newBooking = { ...editedBooking, [field]: value };
    let nights = differenceInDays(
      new Date(newBooking.end_date),
      new Date(newBooking.start_date),
    );
    if (nights <= 0) {
      const endDatePlusOne = format(
        new Date(
          new Date(newBooking.start_date).getTime() + 24 * 60 * 60 * 1000,
        ),
        "yyyy-MM-dd",
      );
      newBooking = {
        ...editedBooking,
        start_date: value,
        end_date: endDatePlusOne,
      };
      nights = 1; //differenceInDays(new Date(newBooking.end_date), new Date(newBooking.start_date));
    }
    const totalPrice = calculateTotalPrice(
      newBooking.pricing_structure_json,
      nights,
      selectedLodging?.pricing_structure_json,
    );
    setEditedBooking({
      ...newBooking,
      nights,
      pricing_structure_json: {
        ...newBooking.pricing_structure_json,
        total_price: totalPrice,
      },
    } as TCalendarBooking);
    setSaveError(null);
  };

  const handleGuestInfoChange = (
    field: "guest_name" | "guest_email" | "guest_phone" | "notes",
    value: string,
  ) => {
    setEditedBooking({ ...editedBooking, [field]: value } as TCalendarBooking);
    setSaveError(null);
  };

  const handleSave = async () => {
    //validate required fields
    if (
      !editedBooking.guest_name ||
      !editedBooking.guest_email ||
      !editedBooking.start_date ||
      !editedBooking.end_date ||
      !editedBooking.lodging_id
    ) {
      setSaveError("Please fill in all required fields.");
      return;
    }
    //validate date logic and non-historical dates
    const today = new Date(); // set to start of today for accurate comparison
    today.setHours(0, 0, 0, 0);
    if (
      new Date(editedBooking.start_date) < today ||
      new Date(editedBooking.end_date) < today
    ) {
      setSaveError("Dates cannot be in the past.");
      return;
    }
    if (
      new Date(editedBooking.start_date) >= new Date(editedBooking.end_date)
    ) {
      setSaveError("Departure date must be after arrival date.");
      return;
    }

    //verify selected lodging max occupancy is not exceeded by number of guests
    const selectedLodging: TLodging | undefined = authStore
      .getLodgings()
      .find((l: TLodging) => l.id === editedBooking.lodging_id);

    if (
      selectedLodging &&
      editedBooking.guests > selectedLodging.max_occupancy
    ) {
      setSaveError(
        `Selected site has a maximum occupancy of ${selectedLodging.max_occupancy} guests.`,
      );
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const response = await bookingStore.saveBooking(editedBooking);
      setIsEditing(false);
      // return;
      //clear cache
      const { days: daysInView, rangeStart, rangeEnd } = props.getViewDays();
      const fromDate = format(rangeStart, "yyyy-MM-dd");
      const toDate = format(rangeEnd, "yyyy-MM-dd");
      bookingStore.clearCache(fromDate, toDate);
      bookingStore.clearAvailabilityCache(fromDate, toDate);
      //show success message and refresh bookings in calendar view
      props.clearBookingToView();
      bookingStore.fetchCalendarBookings(fromDate, toDate);
      bookingStore.fetchAvailability(fromDate, toDate);
    } catch (error) {
      setSaveError(
        error instanceof Error
          ? error.message
          : "An error occurred while saving",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCapturePayment = async () => {
    setIsSaving(true);
    setSaveError(null);

    try {
      const response = await bookingStore.capturePayment(
        editedBooking,
        paymentAmount,
        paymentReference,
      );
      setIsEditing(false);
      // return;
      //clear cache
      const { days: daysInView, rangeStart, rangeEnd } = props.getViewDays();
      const fromDate = format(rangeStart, "yyyy-MM-dd");
      const toDate = format(rangeEnd, "yyyy-MM-dd");
      bookingStore.clearCache(fromDate, toDate);
      bookingStore.clearAvailabilityCache(fromDate, toDate);
      //show success message and refresh bookings in calendar view
      props.clearBookingToView();
      bookingStore.fetchCalendarBookings(fromDate, toDate);
    } catch (error) {
      setSaveError(
        error instanceof Error
          ? error.message
          : "An error occurred while saving",
      );
    } finally {
      setIsSaving(false);
      setShowCaptureDepositConfirm(false);
    }
  };

  const handleMarkConfirmed = async () => {
    setIsSaving(true);
    setSaveError(null);

    try {
      await bookingStore.confirmBooking(editedBooking);
      //clear cache
      const { days: daysInView, rangeStart, rangeEnd } = props.getViewDays();
      const fromDate = format(rangeStart, "yyyy-MM-dd");
      const toDate = format(rangeEnd, "yyyy-MM-dd");
      bookingStore.clearCache(fromDate, toDate);
      bookingStore.clearAvailabilityCache(fromDate, toDate);
      //show success message and refresh bookings in calendar view
      props.clearBookingToView();
      bookingStore.fetchCalendarBookings(fromDate, toDate);
    } catch (error) {
      setSaveError(
        error instanceof Error
          ? error.message
          : "An error occurred while confirming the booking",
      );
    } finally {
      setIsSaving(false);
      setShowMarkConfirmedConfirm(false);
    }
  };

  const handleBookingCancel = async () => {
    setIsSaving(true);
    setSaveError(null);

    try {
      await bookingStore.cancelBooking(editedBooking);
      //clear cache
      const { days: daysInView, rangeStart, rangeEnd } = props.getViewDays();
      const fromDate = format(rangeStart, "yyyy-MM-dd");
      const toDate = format(rangeEnd, "yyyy-MM-dd");
      bookingStore.clearCache(fromDate, toDate);
      bookingStore.clearAvailabilityCache(fromDate, toDate);
      //show success message and refresh bookings in calendar view
      props.clearBookingToView();
      bookingStore.fetchCalendarBookings(fromDate, toDate);
    } catch (error) {
      setSaveError(
        error instanceof Error
          ? error.message
          : "An error occurred while canceling the booking",
      );
    } finally {
      setIsSaving(false);
      setShowCancelConfirm(false);
    }
  };

  const selectedLodging: TLodging | undefined = authStore
    .getLodgings()
    .find((l: TLodging) => l.id === editedBooking.lodging_id);

  const confirmedOrPaid =
    editedBooking.status === "confirmed" || editedBooking.paid;
  const isPaid = editedBooking.paid;

  return (
    <div className="booking-view-container">
      <button onClick={() => props.clearBookingToView()} className="nav-button">
        <i className="fa-solid fa-chevron-left"></i>
      </button>

      <div className="booking-card">
        <div className="booking-card-header">
          <h2>
            {editedBooking.booking_id
              ? "Booking #" + editedBooking.booking_id
              : "New Booking"}
          </h2>
        </div>

        <div className="booking-card-subheader">
          <h5 className={`${editedBooking.status}-booking`}>
            {editedBooking.status === "confirmed" ? (
              <span className="confirmed-booking"><i className="fa-solid fa-circle-check"></i> {editedBooking.nights} night(s) &nbsp;&nbsp;{" "}
                <i className="fa-solid fa-person"></i> {editedBooking.guests ?? "0"}{" "}</span>
            ) : (
              <span>{editedBooking.nights} night(s) &nbsp;&nbsp;{" "}<i className="fa-solid fa-person"></i> {editedBooking.guests ?? "0"}{" "}</span>
            )}{" "}


            &nbsp;&nbsp; <span className={!isPaid ? "" : "confirmed-booking"}><i className="fa-solid fa-coins"></i> R{" "}
              {editedBooking.pricing_structure_json?.total_price ?? "0"}{" "}</span>
            {totalPaid ?
              <>&nbsp;&nbsp; <i className="fa-solid fa-circle-check confirmed-booking"></i> <span className="confirmed-booking">R{" "}
                {totalPaid ?? "0"}</span></>
              : null}

            {balanceDue ?
              <>&nbsp;&nbsp; <i className="fa-solid fa-coins balance-due"></i> <span className="balance-due">R{" "}
                {balanceDue ?? "0"}</span></>
              : null}
          </h5>
        </div>

        {saveError && (
          <div className="error-message">
            <i className="fa-solid fa-circle-exclamation"></i> {saveError}
          </div>
        )}

        <div className="booking-info-section">
          <form className="booking-form">
            <div className="form-group">
              <label htmlFor="guest_name">Guest Name *</label>
              <input
                id="guest_name"
                type="text"
                value={editedBooking.guest_name || ""}
                onChange={(e) =>
                  handleGuestInfoChange("guest_name", e.target.value)
                }
                className="form-input"
                required
                disabled={editedBooking.booking_id ? true : false}
              />
            </div>
            <div className="form-group">
              <label htmlFor="guest_email">Email *</label>
              <input
                id="guest_email"
                type="email"
                value={editedBooking.guest_email || ""}
                onChange={(e) =>
                  handleGuestInfoChange("guest_email", e.target.value)
                }
                className="form-input"
                required
                disabled={editedBooking.booking_id ? true : false}
              />
            </div>
            <div className="form-group">
              <label htmlFor="guest_phone">Phone</label>
              <input
                id="guest_phone"
                type="tel"
                value={editedBooking.guest_phone || ""}
                onChange={(e) =>
                  handleGuestInfoChange("guest_phone", e.target.value)
                }
                className="form-input"
                disabled={editedBooking.booking_id ? true : false}
              />
            </div>
          </form>
        </div>

        <div className="booking-info-section">
          <form className="booking-form">
            <div className="form-group">
              <label htmlFor="lodging_id">Site *</label>
              <select
                id="lodging_id"
                value={editedBooking.lodging_id || ""}
                onChange={(e) => {
                  handleLodgingChange(parseInt(e.target.value));
                }}
                className="form-input"
                required
                disabled={editedBooking.booking_id ? true : false}
              >
                <option value="">Select available site</option>
                {editedBooking.booking_id ?
                  <option key={editedBooking.lodging_id} value={editedBooking.lodging_id}>
                    {editedBooking.lodging_name}
                  </option> : <>
                    {authStore.getLodgings() &&
                      authStore.getLodgings().filter((lodging: TLodging) =>
                        getAvailableLodgingsForDateRange().includes(lodging.id)
                      ).map((lodging: TLodging) => (
                        <option key={lodging.id} value={lodging.id}>
                          {lodging.name} {lodging.description}
                        </option>
                      ))}
                  </>}

              </select>
            </div>

            <div className="form-group">
              <label htmlFor="start_date">Arrival Date *</label>
              <input
                id="start_date"
                type="date"
                value={editedBooking.start_date || ""}
                onChange={(e) => handleDateChange("start_date", e.target.value)}
                className="form-input"
                required
                disabled={editedBooking.booking_id ? true : false}
              />
            </div>
            <div className="form-group">
              <label htmlFor="end_date">Departure Date *</label>
              <input
                id="end_date"
                type="date"
                value={editedBooking.end_date || ""}
                onChange={(e) => handleDateChange("end_date", e.target.value)}
                className="form-input"
                required
                disabled={editedBooking.booking_id ? true : false}
              />
            </div>
          </form>
        </div>

        <div className="booking-info-section">
          {selectedLodging ? (
            <BookingPricingStructure
              setEditedBooking={setEditedBooking}
              editedBooking={editedBooking}
              selectedLodging={selectedLodging}
              handleInputChange={handlePricingStructureChange}
            />
          ) : null}
        </div>

        <div className="booking-info-section">
          <form className="booking-form">
            <div className="form-group">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                value={editedBooking.notes || ""}
                onChange={(e) => handleGuestInfoChange("notes", e.target.value)}
                className="form-input"
                rows={3}
                disabled={editedBooking.booking_id ? true : false}
                placeholder="Any relevant notes..."
              ></textarea>
            </div>
          </form>
        </div>

        {editedBooking.booking_id ? null : (
          <div className="text-left">
            <small>
              {editedBooking.nights} night(s)&nbsp;&nbsp;
              <i className="fa-solid fa-person"></i>{" "}
              {editedBooking.guests ?? "0"}{" "}
              <i className="fa-solid fa-coins"></i>&nbsp;&nbsp;R{" "}
              {editedBooking.pricing_structure_json?.total_price ?? 0}
            </small>
          </div>
        )}

        {editedBooking.booking_id ? (
          <>

            {confirmedOrPaid ?
              <>
                <div className="form-group">
                  <label htmlFor="deposit">Booking payments</label>
                  <BookingPayments
                    bookingPayments={editedBooking.payments || []}
                  />
                </div>
              </> : null}

            {isPaid || balanceDue === 0 ? null :
              <>
                <div>
                  <h4>Capture Payments</h4>
                  <input
                    id="payment_amount"
                    type="number"
                    value={paymentAmount}
                    onChange={(e) =>
                      setPaymentAmount(parseFloat(e.target.value) || 0)
                    }
                    max={balanceDue}
                    className="form-input payment-inputs"
                    required

                  />
                  <input
                    id="payment_ref"
                    type="text"
                    value={paymentReference}
                    onChange={(e) => setPaymentReference(e.target.value)}
                    className="form-input"
                    required
                  // disabled={confirmedOrPaid ? true : false}
                  />
                  <button
                    onClick={() => setShowCaptureDepositConfirm(true)}
                    className="btn btn-primary btn-sm"
                    disabled={isSaving}
                  >
                    <>
                      <i className="fa-solid fa-coins"></i> Capture Payment
                    </>
                  </button>
                </div>
                <div className="form-actions">
                  <button
                    onClick={() => setShowMarkConfirmedConfirm(true)}
                    className="btn btn-info btn-sm"
                    disabled={isSaving}
                  >
                    {editedBooking.status === "confirmed" ? (
                      <>
                        <i className="fa-regular fa-circle-check"></i> Confirmed
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-coins"></i> Mark Confirmed
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setShowCancelConfirm(true)}
                    className="btn btn-danger btn-sm"
                    disabled={isSaving}
                  >
                    <i className="fa-regular fa-circle-xmark"></i> Cancel Booking
                  </button>
                </div></>
            }
          </>
        ) : (
          <div className="form-actions">
            <button
              onClick={handleSave}
              className="btn btn-primary btn-sm"
              disabled={isSaving || !editedBooking.guest_name || !editedBooking.guest_email || !editedBooking.start_date || !editedBooking.end_date || !editedBooking.lodging_id || (editedBooking.start_date < format(new Date(), "yyyy-MM-dd")) || (editedBooking.end_date <= editedBooking.start_date)}
            >
              <i className="fa-regular fa-floppy-disk"></i>{" "}
              {isSaving ? "Saving..." : "Save Booking"}
            </button>
            <button
              onClick={() => props.clearBookingToView()}
              className="btn btn-secondary btn-sm"
              disabled={isSaving}
            >
              Discard
            </button>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={showCancelConfirm}
        title="Cancel Booking"
        message={`Are you sure you want to cancel booking #${editedBooking.booking_id}? This action cannot be undone.`}
        confirmBtnText="Cancel Booking"
        cancelBtnText="Keep Booking"
        onConfirm={handleBookingCancel}
        onCancel={() => setShowCancelConfirm(false)}
        isDangerous={true}
      />

      <ConfirmationModal
        isOpen={showCaptureDepositConfirm}
        title="Capture Deposit"
        message={`Capture R${paymentAmount.toFixed(2)} payment with reference "${paymentReference}"?`}
        confirmBtnText="Capture Payment"
        cancelBtnText="Cancel"
        onConfirm={handleCapturePayment}
        onCancel={() => setShowCaptureDepositConfirm(false)}
      />

      <ConfirmationModal
        isOpen={showMarkConfirmedConfirm}
        title="Mark Booking as Confirmed"
        message={`Mark booking #${editedBooking.booking_id} as confirmed?`}
        confirmBtnText="Mark Confirmed"
        cancelBtnText="Cancel"
        onConfirm={handleMarkConfirmed}
        onCancel={() => setShowMarkConfirmedConfirm(false)}
      />
    </div>
  );
};
